/**
 * WorldSwitcher — moving between HOTTR worlds.
 *
 * Screen System §2: the wordmark suffix IS the control — `HOTTR.app ⌄`. The
 * suffix is already the only part of the wordmark that changes between sites,
 * so the switcher needs no new furniture and reads the same at every
 * breakpoint. Adding a separate globe button would have been a second thing to
 * learn for a job the wordmark was already doing.
 *
 * The panel opens full-bleed under the header, two columns, 1px hairline gaps.
 * The current world is marked with a 2px pink left edge and never a fill —
 * pink's six jobs include "active state", not "surface tint".
 *
 * Each world shows its `role` as a tagline. Its `verb` is deliberately NOT
 * shown: the verb is the internal identity decision that drives the Orb's
 * light and the product's motion, and putting it on screen would turn a design
 * constraint into marketing copy.
 *
 * RENDERS A FRAGMENT — a trigger plus a panel, not one wrapped element. The
 * panel is positioned against the nearest positioned ancestor, and it has to
 * span the full header rather than the width of the wordmark. Put this inside
 * a header that is `position: relative`.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import './chassis.css';
import { cn } from './cn';
import { Icon } from './Icon';
import { Wordmark } from './Wordmark';
import { subscribeWorldsMenu } from './worldsControl';
import { SWITCHABLE_WORLDS, WORLDS, worldHopHref, type HottrProduct } from './worlds';

/**
 * 20px, matching what `.hottr-lockup` set before the type rules moved into
 * <Wordmark>. Pinned rather than left on the `lg` step (18px) so this change
 * fixes the spacing without also resizing the header on nine sites — a
 * correction should not smuggle in a second visual decision.
 */
const LOCKUP_SIZE = 'text-[20px]';

export interface WorldSwitcherProps {
  /** The world this surface is. Marked in the panel, never selectable out of. */
  current: HottrProduct;
  /**
   * Which worlds to offer. Defaults to SWITCHABLE_WORLDS — the nine live
   * consumer products. Pass your own list only when a surface has a real
   * reason to differ: the default is what stops nine apps each leaking a
   * different opinion about who belongs in the menu.
   */
  worlds?: HottrProduct[];
  /**
   * Override where a world lives. Rarely needed — the canonical domains are in
   * kit/worlds.ts and are used automatically, which is what stops nine apps
   * each hardcoding `https://hottr.${suffix}` and each getting Passport, the
   * brand site and the gateway wrong in the same way.
   *
   * Return null to render a world as present-but-unavailable.
   */
  hrefFor?: (product: HottrProduct) => string | null;
  /**
   * Called when a world is chosen — including the one you are already in,
   * which is how a surface keeps a route home after the wordmark becomes the
   * switcher. Handle `product === current` by navigating to that world's
   * in-app home; ignore it and there may be no way back.
   */
  onSelect?: (product: HottrProduct) => void;
  /** Controlled open state. Omit to let the component own it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export const WorldSwitcher = ({
  current,
  worlds = SWITCHABLE_WORLDS,
  hrefFor,
  onSelect,
  open: openProp,
  onOpenChange,
  className,
}: WorldSwitcherProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const panelId = useId().replace(/:/g, '');

  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  // The WORLDS item in the Orb's tail is the other door into this panel.
  useEffect(() => {
    return subscribeWorldsMenu({
      open: () => setOpen(true),
      close: () => setOpen(false),
    });
    // setOpen closes over onOpenChange; re-subscribe if the parent handler changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const entry = (product: HottrProduct): ReactNode => {
    const world = WORLDS[product];
    const isCurrent = product === current;
    const body = (
      <>
        <Wordmark suffix={world.suffix} size="lg" className={LOCKUP_SIZE} />
        <span className="hottr-worldswitch__tagline">{world.role}</span>
      </>
    );

    const href = (hrefFor ?? worldHopHref)(product);

    const shared = {
      className: 'hottr-worldswitch__item',
      'aria-current': isCurrent ? ('true' as const) : undefined,
      'data-unavailable': !isCurrent && !href ? ('true' as const) : undefined,
    };

    // A world with no live home renders as present but inert. Showing it keeps
    // the ecosystem legible; linking it would send someone to a dead address.
    if (!isCurrent && !href) {
      return (
        <div key={product} {...shared} aria-disabled="true">
          {body}
        </div>
      );
    }

    return href && !isCurrent ? (
      <a key={product} href={href} {...shared}>
        {body}
      </a>
    ) : (
      <button
        key={product}
        type="button"
        {...shared}
        onClick={() => {
          setOpen(false);
          // Fires for the CURRENT world too. The wordmark used to be the way
          // home, and replacing it with this control removed that route — on
          // .travel it orphaned four pages whose only entry point was the
          // dashboard. Tapping the world you are already in is the natural
          // "take me to the start of this world", so onSelect carries it and
          // the app decides where home is.
          onSelect?.(product);
        }}
      >
        {body}
      </button>
    );
  };

  return (
    <>
      {/* The lockup is the control, so the chevron sits OUTSIDE it. `.hottr-lockup`
          now carries only button chrome and the 44px touch floor — the gap it
          used to apply fell between HOTTR, the fullstop and the suffix, and drew
          the wordmark as `HOTTR . care`. Spacing here separates the wordmark
          from the chevron, which is a different job. */}
      <button
        type="button"
        className={cn('hottr-lockup', className)}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
      >
        <Wordmark suffix={WORLDS[current].suffix} size="lg" className={LOCKUP_SIZE} />
        <Icon name="expand_more" size={16} />
      </button>

      {open && (
        <div id={panelId} className="hottr-worldswitch" role="menu" aria-label="Worlds">
          {worlds.map(entry)}
        </div>
      )}
    </>
  );
};
